"use client";

import { useAuth } from "@/components/AuthContext";
import { getTutorImageUrl } from "@/lib/school/tutorImages";
import { getTutorFollowupLine, getTutorOpeningLine } from "@/lib/school/tutorPresentation";
import { supabase } from "@/lib/supabaseClient";
import { Tutor, TutorCurriculumItem } from "@/types/tutor";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  BookOpenCheck,
  CheckCircle2,
  Compass,
  MessageCircle,
  Plus,
  Route,
  Search,
  Sparkles,
  Star,
  Target,
  X,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CreateTutorModal from "./CreateTutorModal";
import TutorCard from "./TutorCard";

interface AISchoolViewProps {
  onStartSession: (tutor: Tutor, tutorCurriculumItemId?: string | null) => void;
}

const CATEGORIES = [
  "All",
  "Featured",
  "Language",
  "Academic",
  "Coding",
  "Roleplay",
  "Zen",
  "Wellness",
  "General",
];

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  All: "Every available tutor",
  Featured: "Strongest starting points",
  Language: "Conversation and fluency",
  Academic: "Subjects and exams",
  Coding: "Build and debug",
  Roleplay: "Practice real situations",
  Zen: "Guided calm and reflection",
  Wellness: "Calm reflective sessions",
  General: "Flexible custom help",
};

function normalizeTutor(tutor: Tutor): Tutor {
  return {
    ...tutor,
    rating_avg: Number(tutor.rating_avg || 0),
    rating_count: Number(tutor.rating_count || 0),
    usage_count: Number(tutor.usage_count || 0),
    quality_score:
      typeof tutor.quality_score === "number"
        ? tutor.quality_score
        : Number(tutor.quality_score || 0),
  };
}

function getSampleExchange(tutor: Tutor) {
  const sample = tutor.builder_spec?.sampleExchange;
  if (sample && sample.length >= 2) return sample.slice(0, 4);
  return [
    { role: "tutor" as const, text: getTutorOpeningLine(tutor) },
    {
      role: "learner" as const,
      text: "I understand the idea, but I freeze when I try to use it.",
    },
    {
      role: "tutor" as const,
      text: getTutorFollowupLine(tutor),
    },
  ];
}

function TutorDetailModal({
  tutor,
  onClose,
  onStart,
}: {
  tutor: Tutor;
  onClose: () => void;
  onStart: (tutor: Tutor, tutorCurriculumItemId?: string | null) => void;
}) {
  const curriculum = (tutor.curriculum_items || []).sort((a, b) => a.position - b.position);
  const firstLesson = curriculum[0];
  const sampleExchange = getSampleExchange(tutor);
  const tutorImageUrl = getTutorImageUrl(tutor);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/78 p-0 sm:items-center sm:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.section
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 24, opacity: 0 }}
        className="max-h-[92dvh] w-full max-w-5xl overflow-y-auto rounded-t-lg border border-white/10 bg-[#15161B] text-white shadow-2xl sm:rounded-lg"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#15161B]/96 px-5 py-4 sm:px-7">
          <div>
            <p className="text-xs font-black uppercase text-[#FD954F]">{tutor.category}</p>
            <h2 className="text-2xl font-black tracking-normal">{tutor.name}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-[#1B1C22] text-white/70 hover:border-white/22 hover:text-white"
            aria-label="Close tutor details"
          >
            <X size={18} />
          </button>
        </div>

        <div className="grid gap-6 p-5 sm:p-7 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-5">
            <div className="border-l-2 border-[#FD954F]/70 pl-5">
              <div className="mb-4 flex flex-wrap items-center gap-2 text-xs font-bold">
                <span className="inline-flex items-center gap-1.5 text-[#FFD0B0]">
                  <Target size={13} />
                  {tutor.archetype?.replace(/_/g, " ") || "custom tutor"}
                </span>
                <span className="inline-flex items-center gap-1.5 text-white/60">
                  <Star size={13} className="text-[#F59E0B]" fill="currentColor" />
                  {Number(tutor.rating_avg || 0).toFixed(1)} from {tutor.rating_count || 0} ratings
                </span>
                {firstLesson && (
                  <span className="inline-flex items-center gap-1.5 text-white/60">
                    <Route size={13} />
                    {curriculum.length} lesson Journey
                  </span>
                )}
              </div>
              <p className="text-lg leading-relaxed text-white/82">
                {tutor.description || tutor.goal || "A focused tutor for active practice."}
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="border-t border-white/10 pt-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-black text-white">
                  <CheckCircle2 size={17} className="text-[#22C55E]" />
                  What this tutor is good for
                </h3>
                <p className="text-sm leading-relaxed text-white/68">
                  {tutor.goal ||
                    "Turning a broad learning goal into short attempts, corrections, and next steps."}
                </p>
              </div>
              <div className="border-t border-white/10 pt-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-black text-white">
                  <MessageCircle size={17} className="text-[#FD954F]" />
                  How it teaches
                </h3>
                <p className="text-sm leading-relaxed text-white/68">
                  {tutor.builder_spec?.sessionStyle ||
                    "Starts with your current understanding, gives one task at a time, and repairs the highest-impact issue first."}
                </p>
              </div>
            </div>

            <div className="border-t border-white/10 pt-5">
              <h3 className="mb-4 text-sm font-black text-white">Sample exchange</h3>
              <div className="space-y-3">
                {sampleExchange.map((entry, index) => (
                  <div
                    key={`${entry.role}-${index}`}
                    className={`rounded-lg px-4 py-3 text-sm leading-relaxed ${
                      entry.role === "tutor"
                        ? "text-white/74"
                        : "ml-auto max-w-[86%] border border-white/10 bg-[#1B1C22] text-white/78"
                    }`}
                  >
                    <p className="mb-1 text-[11px] font-black uppercase text-white/35">
                      {entry.role === "tutor" ? tutor.name : "Learner"}
                    </p>
                    {entry.text}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="overflow-hidden rounded-lg border border-white/10 bg-[#15161B]">
              <div className="relative h-64 bg-[#0F0F14]">
                <img
                  src={tutorImageUrl}
                  alt=""
                  className="h-full w-full object-cover object-top"
                  onError={(event) => {
                    event.currentTarget.src = "/ui/Tutors/Phoebe.png";
                  }}
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_48%,rgba(15,15,20,0.94))]" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-xs font-black uppercase text-[#FD954F]">
                    {tutor.archetype?.replace(/_/g, " ") || "AI tutor"}
                  </p>
                  <p className="text-lg font-black text-white">{tutor.name}</p>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onStart(tutor, null)}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#FD954F] text-sm font-black text-[#1C1C1E] hover:bg-[#F28747]"
            >
              Start freeform
              <ArrowRight size={17} />
            </button>
            {firstLesson && (
              <button
                type="button"
                onClick={() => onStart(tutor, firstLesson.id)}
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg border border-[#FD954F]/35 bg-[#FD954F]/8 text-sm font-black text-[#FFD0B0] hover:bg-[#FD954F]/14"
              >
                Start Journey lesson 1
                <Route size={17} />
              </button>
            )}

            <div className="border-t border-white/10 pt-5">
              <h3 className="mb-4 flex items-center gap-2 text-sm font-black">
                <BookOpenCheck size={17} className="text-[#FD954F]" />
                Journey
              </h3>
              {curriculum.length > 0 ? (
                <div className="space-y-3">
                  {curriculum.slice(0, 5).map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() => onStart(tutor, item.id)}
                      className="block w-full border-b border-white/8 px-1 py-3 text-left last:border-b-0 hover:bg-white/[0.025]"
                    >
                      <p className="text-xs font-black text-[#FD954F]">Lesson {item.position}</p>
                      <p className="mt-1 text-sm font-bold text-white">{item.title}</p>
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-white/52">
                        {item.objective}
                      </p>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm leading-relaxed text-white/58">
                  This tutor starts in freeform mode. Custom tutors can add a short Journey during
                  creation.
                </p>
              )}
            </div>
          </aside>
        </div>
      </motion.section>
    </motion.div>
  );
}

const AISchoolView: React.FC<AISchoolViewProps> = ({ onStartSession }) => {
  const { user, openAuthModal } = useAuth();
  const posthog = usePostHog();
  const [activeCategory, setActiveCategory] = useState("All");
  const [tutors, setTutors] = useState<Tutor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState<Tutor | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [userRatings, setUserRatings] = useState<Record<string, number>>({});
  const [ratingTutorId, setRatingTutorId] = useState<string | null>(null);

  const fetchTutors = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("tutors")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      const tutorRows = ((data as Tutor[]) || []).map(normalizeTutor);

      try {
        const tutorIds = tutorRows.map((t) => t.id);
        if (tutorIds.length > 0) {
          const { data: curriculumData, error: curriculumError } = await supabase
            .from("tutor_curriculum_items")
            .select("*")
            .in("tutor_id", tutorIds)
            .order("position", { ascending: true });

          if (!curriculumError && curriculumData) {
            const byTutor = (curriculumData as TutorCurriculumItem[]).reduce(
              (acc, item) => {
                acc[item.tutor_id] = acc[item.tutor_id] || [];
                acc[item.tutor_id].push(item);
                return acc;
              },
              {} as Record<string, TutorCurriculumItem[]>
            );
            tutorRows.forEach((tutor) => {
              tutor.curriculum_items = byTutor[tutor.id] || [];
              tutor.curriculum_count = tutor.curriculum_items.length;
            });
          }
        }
      } catch {
        // Curriculum table is additive; browsing tutors should still work before SQL is applied.
      }

      setTutors(tutorRows);

      if (user && tutorRows.length > 0) {
        const tutorIds = tutorRows.map((t) => t.id);
        const { data: ratingsData, error: ratingsError } = await supabase
          .from("tutor_ratings")
          .select("tutor_id, rating")
          .eq("user_id", user.id)
          .in("tutor_id", tutorIds);

        if (ratingsError) throw ratingsError;

        const ratingsMap = (ratingsData || []).reduce(
          (acc, row) => {
            acc[row.tutor_id] = row.rating;
            return acc;
          },
          {} as Record<string, number>
        );
        setUserRatings(ratingsMap);
      } else {
        setUserRatings({});
      }
    } catch (err) {
      console.error("Error fetching tutors:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchTutors();
  }, [fetchTutors]);

  const handleOpenCreate = () => {
    if (!user) {
      openAuthModal("signin");
      return;
    }
    posthog?.capture("school_builder_started", { source: "school_header" });
    setShowCreateModal(true);
  };

  const handleRateTutor = async (tutorId: string, rating: number) => {
    if (!user) {
      openAuthModal("signin");
      return;
    }

    if (rating < 1 || rating > 5) return;

    setRatingTutorId(tutorId);
    try {
      const { error } = await supabase.from("tutor_ratings").upsert(
        {
          tutor_id: tutorId,
          user_id: user.id,
          rating,
        },
        {
          onConflict: "tutor_id,user_id",
        }
      );

      if (error) throw error;
      posthog?.capture("school_tutor_rated", { tutor_id: tutorId, rating });
      setUserRatings((prev) => ({ ...prev, [tutorId]: rating }));
      await fetchTutors();
    } catch (err) {
      console.error("Error submitting tutor rating:", err);
    } finally {
      setRatingTutorId(null);
    }
  };

  const handleStart = (tutor: Tutor, tutorCurriculumItemId?: string | null) => {
    posthog?.capture(
      tutorCurriculumItemId ? "school_journey_lesson_started" : "school_freeform_session_started",
      {
        tutor_id: tutor.id,
        tutor_name: tutor.name,
        category: tutor.category,
        has_journey: Boolean(tutor.curriculum_count),
        tutor_curriculum_item_id: tutorCurriculumItemId || null,
      }
    );
    onStartSession(tutor, tutorCurriculumItemId || null);
  };

  const filteredTutors = useMemo(() => {
    return tutors.filter((t) => {
      const matchesCategory =
        activeCategory === "All" ||
        (activeCategory === "Featured"
          ? Boolean(t.quality_score && t.quality_score >= 80) ||
            Number(t.rating_avg || 0) >= 4.5 ||
            Boolean(t.curriculum_count)
          : t.category === activeCategory);
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        t.name.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q) ||
        t.goal?.toLowerCase().includes(q) ||
        t.archetype?.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery, tutors]);

  const featuredTutors = useMemo(
    () =>
      [...tutors]
        .sort(
          (a, b) =>
            Number(b.curriculum_count || 0) - Number(a.curriculum_count || 0) ||
            Number(b.quality_score || 0) - Number(a.quality_score || 0) ||
            Number(b.rating_avg || 0) - Number(a.rating_avg || 0)
        )
        .slice(0, 4),
    [tutors]
  );

  const catalogTutors = useMemo(() => {
    if (activeCategory !== "All" || searchQuery) return filteredTutors;
    const featuredIds = new Set(featuredTutors.map((tutor) => tutor.id));
    return filteredTutors.filter((tutor) => !featuredIds.has(tutor.id));
  }, [activeCategory, featuredTutors, filteredTutors, searchQuery]);

  return (
    <div className="min-h-screen bg-[#0F0F14] text-white">
      <div className="relative z-10 border-b border-white/10 bg-[#0F0F14]">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="mb-2 inline-flex items-center gap-2 text-xs font-black uppercase text-[#FD954F]">
                <Compass size={14} />
                AI School
              </p>
              <h1 className="text-3xl font-black leading-tight sm:text-4xl">
                Find the right tutor. Or build one.
              </h1>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-white/68 sm:text-lg">
                Browse focused AI tutors with distinct teaching styles. Start a conversation or
                follow a structured Journey.
              </p>
            </div>

            <button
              type="button"
              onClick={handleOpenCreate}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-[#FD954F] px-5 text-sm font-black text-[#1C1C1E] hover:bg-[#F28747]"
            >
              <Plus size={18} />
              Create a tutor
            </button>
          </div>

          <div className="mt-5 flex flex-col gap-3 border-t border-white/10 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-1 overflow-x-auto rounded-lg border border-white/10 bg-[#15161B] p-1">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  title={CATEGORY_DESCRIPTIONS[cat]}
                  className={`h-10 whitespace-nowrap rounded-md px-4 text-sm font-bold transition-all ${
                    activeCategory === cat
                      ? "bg-white text-[#1C1C1E]"
                      : "text-white/54 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="relative w-full sm:w-80">
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/35"
              />
              <input
                type="text"
                placeholder="Find a tutor, skill, or goal"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-11 w-full rounded-lg border border-white/10 bg-[#15161B] pl-11 pr-4 text-sm font-semibold text-white outline-none transition-all placeholder:text-white/35 focus:border-[#FD954F]/55"
              />
            </div>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-8 pb-32 sm:px-6">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="h-9 w-9 rounded-full border-2 border-white/20 border-t-[#FD954F] animate-spin" />
          </div>
        ) : (
          <div className="space-y-10">
            {featuredTutors.length > 0 && !searchQuery && activeCategory === "All" && (
              <section>
                <div className="mb-4 flex items-end justify-between gap-4">
                  <div>
                    <p className="text-xs font-black uppercase text-[#FD954F]">Featured</p>
                    <h2 className="text-2xl font-black tracking-normal">
                      Start with a strong tutor
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveCategory("Featured")}
                    className="hidden text-sm font-bold text-[#FFD0B0] hover:text-white sm:inline-flex"
                  >
                    View all featured
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                  {featuredTutors.map((tutor) => (
                    <TutorCard
                      key={tutor.id}
                      tutor={tutor}
                      onStart={handleStart}
                      onDetails={setSelectedTutor}
                      userRating={userRatings[tutor.id]}
                      onRate={handleRateTutor}
                      isRating={ratingTutorId === tutor.id}
                      featured
                    />
                  ))}
                </div>
              </section>
            )}

            {(activeCategory !== "All" || Boolean(searchQuery) || catalogTutors.length > 0) && (
              <section>
                <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
                  <div>
                    <p className="text-xs font-black uppercase text-[#FD954F]">{activeCategory}</p>
                    <h2 className="text-2xl font-black tracking-normal">
                      {catalogTutors.length} available tutor{catalogTutors.length === 1 ? "" : "s"}
                    </h2>
                    <p className="mt-1 text-sm text-white/52">
                      {CATEGORY_DESCRIPTIONS[activeCategory] ||
                        "Choose a tutor and start practicing."}
                    </p>
                  </div>
                </div>

                {catalogTutors.length > 0 ? (
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {catalogTutors.map((tutor) => (
                      <TutorCard
                        key={tutor.id}
                        tutor={tutor}
                        onStart={handleStart}
                        onDetails={setSelectedTutor}
                        userRating={userRatings[tutor.id]}
                        onRate={handleRateTutor}
                        isRating={ratingTutorId === tutor.id}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="border-t border-white/10 px-6 py-16 text-center">
                    <Sparkles className="mx-auto mb-4 text-[#FD954F]" size={34} />
                    <h3 className="text-xl font-black">No tutors found</h3>
                    <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-white/56">
                      Try a broader search, switch categories, or design the tutor you wanted to
                      find.
                    </p>
                    <button
                      type="button"
                      onClick={handleOpenCreate}
                      className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#FD954F] px-4 text-sm font-black text-[#1C1C1E] hover:bg-[#F28747]"
                    >
                      <Plus size={17} />
                      Design your own Tutor
                    </button>
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </main>

      <button
        type="button"
        onClick={handleOpenCreate}
        className="fixed bottom-5 right-5 z-30 inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-[#FD954F] px-5 text-sm font-black text-[#1C1C1E] shadow-xl shadow-black/35 hover:bg-[#F28747] sm:hidden"
      >
        <Plus size={18} />
        Design Tutor
      </button>

      <AnimatePresence>
        {selectedTutor && (
          <TutorDetailModal
            tutor={selectedTutor}
            onClose={() => setSelectedTutor(null)}
            onStart={(tutor, tutorCurriculumItemId) => {
              setSelectedTutor(null);
              handleStart(tutor, tutorCurriculumItemId);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCreateModal && (
          <CreateTutorModal
            onClose={() => setShowCreateModal(false)}
            onCreated={() => {
              setShowCreateModal(false);
              fetchTutors();
              posthog?.capture("school_tutor_saved");
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AISchoolView;
